/**
 * URL-transform strategies (mirrors `Strategy/CloudflareResizingStrategy` +
 * `Strategy/PassthroughStrategy`).
 *
 * The Cloudflare strategy emits the SAME `/cdn-cgi/image/` form the PHP
 * `CloudflareResizingStrategy::transform()` produces — byte-identical so the
 * client-side URLs match the server-rendered ones (the non-negotiable invariant
 * locked by the cross-seam test, doc 21 §5.1). The passthrough strategy returns
 * the source unchanged and reports `producesDistinctWidths() === false`, which
 * drives the builder's degenerate-srcset guard.
 *
 * Side-effect-free: factories only — origin resolution lives in `apply`/
 * `transform`, never at module top level (doc 21 §9 step 3 / AC4).
 *
 * @see src/Services/ResponsiveImage/Strategy/CloudflareResizingStrategy.php (the byte-identical form)
 * @see src/Services/ResponsiveImage/Strategy/PassthroughStrategy.php
 */
import { QUALITY } from './config';
import type { Strategy } from './types';

/** A `/cdn-cgi/image/` URL is already CF-transformed — never double-wrap (PHP idempotency). */
function isAlreadyTransformed(url: string): boolean {
  return url.includes('/cdn-cgi/image/');
}

/** Srcset-illegal raw whitespace → its percent-encoding (byte-identical to the PHP map). */
const SRCSET_UNSAFE: Record<string, string> = {
  ' ': '%20',
  '\t': '%09',
  '\r': '%0D',
  '\n': '%0A',
};

/**
 * Percent-encode srcset-breaking characters in the source segment — the JS twin
 * of `CloudflareResizingStrategy::encodeSrcsetUnsafeChars()`. A raw space would
 * split the srcset candidate and invalidate the whole attribute. Minimal on
 * purpose: `%` is NOT encoded, so an already-encoded source stays byte-identical
 * (idempotent, no double-encoding).
 */
function encodeSrcsetUnsafeChars(url: string): string {
  return url.replace(/[ \t\r\n]/g, (ch) => SRCSET_UNSAFE[ch]);
}

/**
 * Cloudflare resize strategy bound to a zone `origin`. Wraps a source at width
 * `w` in the form
 *   {origin}/cdn-cgi/image/width={w},format=auto,fit=scale-down,quality={QUALITY},onerror=redirect/{src}
 * An empty or already-transformed source is returned unchanged (idempotent).
 */
export function cloudflareStrategy(origin: string): Strategy {
  const base = origin.replace(/\/$/, '');

  return {
    transform(src: string, width: number): string {
      const trimmed = src.trim();
      if (trimmed === '' || isAlreadyTransformed(trimmed)) {
        return trimmed;
      }
      const options =
        `width=${width},format=auto,fit=scale-down,` +
        `quality=${QUALITY},onerror=redirect`;

      return `${base}/cdn-cgi/image/${options}/${encodeSrcsetUnsafeChars(trimmed)}`;
    },
    producesDistinctWidths(): boolean {
      return true;
    },
  };
}

/**
 * Passthrough strategy (mirrors `PassthroughStrategy`): returns the source
 * unchanged and reports `producesDistinctWidths() === false`, so the builder
 * emits no candidates (the view collapses to the plain `src`). Used in a no-CF
 * context (the JS analogue of `RIMG_STRATEGY=passthrough`).
 */
export const passthroughStrategy: Strategy = {
  transform(src: string): string {
    return src;
  },
  producesDistinctWidths(): boolean {
    return false;
  },
};

/** `{origin}/cdn-cgi/image/{options}/{src}` — the options segment holds no `/`. */
const CLOUDFLARE_WRAPPED = /\/cdn-cgi\/image\/[^/]*\/(.+)$/;

/**
 * The inverse of {@link cloudflareStrategy}'s `transform`: the source a
 * Cloudflare-resize URL wraps, or `null` when `url` is not such a URL.
 *
 * Needed where the ORIGINAL image has to be shown again — a drawing whose
 * marker buttons carry natural-pixel coordinates of the original (the old
 * generator's stored HTML) cannot be placed on a downscaled variant. The
 * returned segment is the source exactly as it was wrapped (percent-encoding
 * of srcset-unsafe characters kept; it addresses the same object).
 */
export function cloudflareSourceOf(url: string): string | null {
  const match = CLOUDFLARE_WRAPPED.exec(url.trim());
  return match ? match[1] : null;
}
